import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.launch
import dev.gitlive.firebase.firestore.Timestamp
import models.DomainGame
import models.BankoBot
import models.BankoGameEngine
import models.BotAction
import models.CompletedGame
import models.DiceRoll
import models.GameRepository
import models.GameSettings
import models.GinkoEvent
import models.LimitedTimeGameModes
import models.Nudge
import models.Player
import models.Round
import models.RoundModifier
import models.Setting
import models.TableReaction
import models.TableReactionKind
import kotlin.random.Random

class BankoGameScreenUseCase(
    private val gameRepository: GameRepository,
    private val scope: CoroutineScope,
    private val gameCode: String,
    private val playerName: String
) {
    private val gameFlow = MutableStateFlow<DomainGame?>(null)
    // The round a roll was last computed and dispatched from. Rolls are computed off the remote
    // snapshot, which only updates after the write round-trips back — so a second roll fired before
    // that return would compute off the same stale state and clobber the first (e.g. a non-bust roll
    // overwriting a bust's round reset, carrying the pot over). We block until the snapshot changes.
    private var lastRolledFromRound: Round? = null
    private val bankListFlow = MutableStateFlow<List<String>>(emptyList())
    private val gameEventFlow = MutableEventStateFlow<GameScreenEvent>()
    private val newPlayerNameInputFlow = MutableStateFlow("")
    private val roundOverlayStateFlow = MutableStateFlow(RoundOverlayState())
    // Snarky play-by-play ticker. Latest line shown until a new beat replaces it.
    private val commentaryFlow = MutableStateFlow<String?>(null)
    private val commentator = Commentator()
    private var previousGameForCommentary: DomainGame? = null
    private var previousRound = -1
    private var gameFinishedEventSent = false
    private var gameCompletionAnalyticsSent = false
    private var hasSeenGame = false
    private var isLeavingGame = false
    private var lobbyViewedLogged = false
    private var previousLobbyGuestCount: Int? = null
    private var firstRollLogged = false
    private var firstBankLogged = false
    // Claimed from the launch marker once, when this screen opens — a stable truth for the whole
    // game (the global marker is cleared immediately so it can't leak into a later game).
    private val isPracticeGame =
        PracticeGameSession.consumeLaunch() && gameCode == GameRepository.LOCAL_GAME_CODE
    private var practiceCompletionHandled = false

    // First-game banking hint: which round the hint is holding the ticker for (so regular
    // commentary doesn't steamroll it while the decision it teaches is still live), whether it
    // fired in this game, and whether the one-time bust follow-up already ran.
    private var bankingHintHoldRoundNum: Int? = null
    private var bankingHintShownThisGame = false
    private var bankingBustReinforced = false
    // Consecutive 7s rolled by this device's player, for the "Lucky" achievement.
    private var consecutiveSevens = 0
    // Lifetime stats fold each finished game in exactly once. A rematch sends the table back to
    // the lobby (round == null), which re-arms recording for the next game on this screen.
    private var careerRecordedForThisGame = false

    // Bots are driven only on the authoritative local device. We track the in-flight bot turn so a
    // burst of flow emissions for the same turn doesn't schedule the action more than once.
    private var botJob: Job? = null
    private var scheduledBotTurn: String? = null

    // The round overlay is sometimes shown after a delay (so a busting roll's dice can animate first).
    private var roundOverlayJob: Job? = null

    // Nudge: a player can buzz whoever's taking their turn. Gated by a cooldown (and a grace period
    // at the start of each turn) so it can't be spammed or fired the instant the turn begins.
    private val canNudgeFlow = MutableStateFlow(true)
    private var nudgeCooldownJob: Job? = null
    private var lastSeenNudgeNonce: Int? = null
    // Whether we've taken the baseline snapshot of the nudge nonce since the screen opened. Set on
    // the first game emission regardless of whether a nudge exists yet, so the first real nudge
    // isn't mistaken for a stale-on-load nudge and silently swallowed.
    private var nudgeBaselineEstablished = false
    private var previousNudgeActivityKey: String? = null
    // Increments each time a nudge aimed at me arrives, so the UI can react visibly (a shake) — the
    // haptic alone is imperceptible on web/desktop.
    private val nudgeReceivedFlow = MutableStateFlow(0)

    // Table reactions are deliberately ephemeral UI events. Firestore keeps only one replaceable
    // event per sender; this screen adopts the initial snapshot silently, then turns fresh ids into
    // at most three short-lived, aggregating bursts over the dice table.
    private val canSendTableReactionFlow = MutableStateFlow(true)
    private val tableReactionsMutedFlow = MutableStateFlow(false)
    private val visibleTableReactionsFlow = MutableStateFlow<List<TableReactionBurst>>(emptyList())
    private val lastSeenReactionEventBySender = mutableMapOf<String, String>()
    private val reactionExpiryJobs = mutableMapOf<String, Job>()
    private var reactionBaselineEstablished = false
    private var reactionAnimationSequence = 0
    private var reactionCooldownJob: Job? = null
    private var previousReactionTurnKey: String? = null

    init {
        // Clear any lobby ad state left over from a previous game until this one's host is known.
        LobbyAdState.claim(this)
        // Invite links wait for the start screen while a game is open. A re-tapped invite for this
        // table is dropped; any other one gets a heads-up so the tap doesn't look ignored.
        scope.launch {
            InviteLinks.pendingInviteCode.collect { code ->
                when {
                    code.isNullOrBlank() -> Unit
                    code.equals(gameCode, ignoreCase = true) -> InviteLinks.consume()
                    else -> gameEventFlow.send(
                        GameScreenEvent.ShowSnackbar("Invite saved: leave this game to join $code.")
                    )
                }
            }
        }
        // Initialize game data collection
        scope.launch {
            gameRepository.domainGameFlow(gameCode)
                .catch { error ->
                    println("Game update listener failed: ${error.message}")
                    gameEventFlow.send(
                        GameScreenEvent.ShowSnackbar(
                            error.userFacingGameActionMessage("Couldn't load game updates.")
                        )
                    )
                }
                .collect { domainGame ->
                if (domainGame == null) {
                    LobbyAdState.publish(this@BankoGameScreenUseCase, false)
                    gameFlow.value = null
                    if (hasSeenGame && !gameFinishedEventSent && !isLeavingGame) {
                        gameEventFlow.send(GameScreenEvent.GameUnavailable)
                    }
                    return@collect
                }
                hasSeenGame = true
                // A Banko Plus host removes ads for the whole lobby; publish that for the ad banner.
                LobbyAdState.publish(this@BankoGameScreenUseCase, domainGame.hostPerksActive)
                // check if game contains current playerName, but only if game is not finished
                if (
                    !isLeavingGame &&
                    domainGame.players.none { it.name == playerName } &&
                    domainGame.finished != true
                ) {
                    gameEventFlow.send(GameScreenEvent.PlayerWasKicked)
                } else if (domainGame.finished == true && !gameFinishedEventSent) {
                    // Game is finished, send game finished event only once
                    gameFinishedEventSent = true
                    gameEventFlow.send(GameScreenEvent.GameFinished)
                    // Report the analytics event from the host only — every game has exactly one
                    // host present, so this counts each finished game once instead of once per device.
                    if (domainGame.host == playerName && !gameCompletionAnalyticsSent) {
                        gameCompletionAnalyticsSent = true
                        Analytics.gameFinished(
                            game = domainGame,
                            offline = gameCode == GameRepository.LOCAL_GAME_CODE,
                        )
                    }
                }
                val sortedGame = domainGame.let { game ->
                    game.copy(
                        players = game.players.sortedBy { player ->
                            game.orderedPlayerNames.indexOf(player.name)
                        }
                    )
                }
                if (sortedGame.round == null && !lobbyViewedLogged) {
                    lobbyViewedLogged = true
                    Analytics.lobbyViewed(
                        role = analyticsRole(sortedGame),
                        humanCount = sortedGame.players.count { !it.isBot },
                        botCount = sortedGame.players.count { it.isBot },
                        offline = gameCode == GameRepository.LOCAL_GAME_CODE,
                    )
                }
                if (sortedGame.round == null && sortedGame.host == playerName) {
                    val guestCount = sortedGame.players.count { !it.hostCreated && !it.isBot }
                    val previousCount = previousLobbyGuestCount
                    if (previousCount != null && guestCount > previousCount) {
                        Analytics.lobbyGuestJoined(
                            guestCount = guestCount,
                            humanCount = sortedGame.players.count { !it.isBot },
                        )
                    }
                    previousLobbyGuestCount = guestCount
                }
                gameFlow.value = sortedGame
                handleReactionTurnChange(sortedGame)
                maybeRecordCareerGame(sortedGame)
                if (hasCreatorPlayer(sortedGame) && Achievements.unlockCreatorDice()) {
                    gameEventFlow.send(GameScreenEvent.ShowSnackbar(CREATOR_DICE_UNLOCKED_MESSAGE))
                }

                // Finishing the practice round (all rounds played, not quit early) earns the
                // Training Wheels dice and ends the session: no game-over ceremony — the screen
                // returns to the start screen with the dice picker open on the new unlock.
                val reachedGameOver = isGameOver(sortedGame)
                if (
                    reachedGameOver &&
                    sortedGame.host == playerName &&
                    !gameCompletionAnalyticsSent
                ) {
                    // Completion is a gameplay state, not the later host teardown action. This
                    // also covers local games and practice, whose data sources do not set finished.
                    gameCompletionAnalyticsSent = true
                    Analytics.gameFinished(
                        game = sortedGame,
                        offline = gameCode == GameRepository.LOCAL_GAME_CODE,
                    )
                }
                if (isPracticeGame && reachedGameOver && !practiceCompletionHandled) {
                    practiceCompletionHandled = true
                    val firstUnlock = Achievements.unlockPracticeDice()
                    PracticeGameSession.requestDiceShowcase()
                    gameEventFlow.send(GameScreenEvent.PracticeCompleted(firstUnlock))
                }

                // Color commentary: spot the dramatic beat between this snapshot and the last —
                // unless the first-game banking hint is holding the ticker (it stays up while the
                // decision it teaches is still live: same round, player still unbanked).
                val hintRound = sortedGame.round
                val selfRiding = hintRound != null &&
                        playerName in hintRound.activeOrderedPlayerNames
                val hintHolding = bankingHintHoldRoundNum != null &&
                        hintRound?.roundNum == bankingHintHoldRoundNum &&
                        selfRiding
                if (!hintHolding) {
                    bankingHintHoldRoundNum = null
                    commentator.reactTo(previousGameForCommentary, sortedGame)?.let { line ->
                        commentaryFlow.value = line
                    }
                }

                // One-time bust follow-up: the hint fired, they rode anyway, and a 7 took their
                // share (round advanced past the safe window with them still in it and their
                // score unchanged — a bank would have raised it). Close the teaching loop once.
                if (bankingHintShownThisGame && !bankingBustReinforced) {
                    val prev = previousGameForCommentary
                    val prevRound = prev?.round
                    val prevPoints = prev?.players?.firstOrNull { it.name == playerName }?.points
                    val nowPoints = sortedGame.players.firstOrNull { it.name == playerName }?.points
                    if (prevRound != null && hintRound != null &&
                        hintRound.roundNum > prevRound.roundNum &&
                        prevRound.currentRoll > 3 &&
                        prevRound.currentPoints > 0 &&
                        playerName in prevRound.activeOrderedPlayerNames &&
                        prevPoints != null && prevPoints == nowPoints
                    ) {
                        bankingBustReinforced = true
                        commentaryFlow.value = BANKING_BUST_MESSAGE
                    }
                }

                // First-game banking hint, once ever: fires the moment banking first becomes a
                // real decision — the round passes roll 3 (a 7 now wipes the pot) while this
                // player is still riding.
                if (hintRound != null && hintRound.currentRoll > 3 && selfRiding &&
                    OnboardingHints.consumeBankingHint()
                ) {
                    bankingHintShownThisGame = true
                    bankingHintHoldRoundNum = hintRound.roundNum
                    commentaryFlow.value = BANKING_HINT_MESSAGE
                }

                previousGameForCommentary = sortedGame

                // Buzz this device if a nudge is aimed at me, and reset the cooldown on turn changes.
                handleNudgeState(domainGame)

                // Let a bot take its turn (after a short, human-like pause) if it's up next.
                maybeScheduleBotTurn(sortedGame)

                // Handle round overlay logic
                val round = domainGame.round
                val currentRound = round?.roundNum ?: 1
                val endRound = domainGame.endRoundNum
                if (round != null && currentRound <= endRound) {
                    // Show overlay if the round changed, or when round 1 first appears.
                    if (previousRound != currentRound || (currentRound == 1 && previousRound == -1)) {
                        previousRound = currentRound
                        val overlayModifier = round.modifier
                        // A bust advances the round on the same emission as the busting roll, whose
                        // dice are carried into the new round — wait for that animation before
                        // covering the board. Round 1 / all-bank advances reset dice to 0, so they
                        // announce immediately.
                        val hasBustDiceToShow = round.dieOne != 0 || round.dieTwo != 0
                        val overlayDelay = if (hasBustDiceToShow) ROLL_ANIMATION_MS else 0L
                        roundOverlayJob?.cancel()
                        roundOverlayJob = scope.launch {
                            delay(overlayDelay)
                            roundOverlayStateFlow.value = RoundOverlayState(
                                showOverlay = true,
                                roundNumber = currentRound,
                                isAnimating = true,
                                modifier = overlayModifier,
                            )
                        }
                    }
                }
            }
        }

        scope.launch {
            gameRepository.tableReactionsFlow(gameCode)
                // Reactions are optional social sugar. If a legacy game or a staged backend has
                // not enabled the subcollection yet, gameplay should continue without a warning.
                .catch { emit(emptyList()) }
                .collect(::handleTableReactionState)
        }
    }

    operator fun invoke(): BankoGameScreenUiState {
        return BankoGameScreenUiState(
            gameCode = gameCode,
            playerName = playerName,
            isPracticeGame = isPracticeGame,
            gameFlow = gameFlow.asStateFlow(),
            bankListFlow = bankListFlow.asStateFlow(),
            gameEventFlow = gameEventFlow,
            newPlayerNameInputFlow = newPlayerNameInputFlow.asStateFlow(),
            roundOverlayStateFlow = roundOverlayStateFlow.asStateFlow(),
            commentaryFlow = commentaryFlow.asStateFlow(),
            canNudgeFlow = canNudgeFlow.asStateFlow(),
            nudgeReceivedFlow = nudgeReceivedFlow.asStateFlow(),
            canSendTableReactionFlow = canSendTableReactionFlow.asStateFlow(),
            tableReactionsMutedFlow = tableReactionsMutedFlow.asStateFlow(),
            visibleTableReactionsFlow = visibleTableReactionsFlow.asStateFlow(),
            onCommentaryLineFinished = { line ->
                if (commentaryFlow.value == line) {
                    commentaryFlow.value = null
                }
            },
            onNudge = {
                val game = gameFlow.value
                val target = game?.currentPlayer
                // Only buzz another player's turn in a network game, once the cooldown has elapsed.
                if (canNudgeFlow.value &&
                    gameCode != "LOCAL" &&
                    !target.isNullOrEmpty() &&
                    target != playerName
                ) {
                    val nextNonce = (game.nudge?.nonce ?: 0) + 1
                    launchGameAction("Couldn't send nudge.") {
                        gameRepository.sendNudge(
                            gameCode,
                            Nudge(targetPlayer = target, nonce = nextNonce),
                        )
                        startNudgeCooldown(NUDGE_COOLDOWN_MS)
                    }
                }
            },
            onTableReaction = { kind ->
                val game = gameFlow.value
                val round = game?.round
                val target = game?.currentPlayer.orEmpty()
                val gameIsLive = game != null &&
                    round != null &&
                    !game.finished &&
                    round.roundNum <= game.endRoundNum
                if (canSendTableReactionFlow.value &&
                    gameCode != GameRepository.LOCAL_GAME_CODE &&
                    gameIsLive &&
                    target.isNotBlank() &&
                    target != playerName
                ) {
                    val activeRound = requireNotNull(round)
                    val now = Timestamp.now()
                    val eventId = buildString {
                        append(playerName)
                        append(':')
                        append(activeRound.roundNum)
                        append(':')
                        append(activeRound.currentRoll)
                        append(':')
                        append(now.seconds)
                        append(':')
                        append(now.nanoseconds)
                        append(':')
                        append(Random.nextInt())
                    }
                    // Close the local tap race before starting the network write. A failed send
                    // still observes the short cooldown, which is calmer than enabling rapid retry.
                    startTableReactionCooldown()
                    launchGameAction("Couldn't send reaction.") {
                        gameRepository.sendTableReaction(
                            gameCode,
                            TableReaction(
                                eventId = eventId,
                                senderPlayer = playerName,
                                targetPlayer = target,
                                kind = kind,
                                roundNum = activeRound.roundNum,
                                currentRoll = activeRound.currentRoll,
                            ),
                        )
                        Analytics.tableReactionSent(kind)
                    }
                }
            },
            onToggleTableReactionsMuted = {
                tableReactionsMutedFlow.value = !tableReactionsMutedFlow.value
                if (tableReactionsMutedFlow.value) clearVisibleTableReactions()
            },
            onStartGame = {
                launchGameAction("Couldn't start game.") {
                    val game = gameFlow.value
                    gameRepository.startGame(
                        gameCode,
                        activeOrderedPlayerNames = game?.orderedPlayerNames ?: emptyList(),
                        firstRoundModifier = nextRoundModifier(),
                    )
                    game?.let {
                        Analytics.gameStarted(
                            playerCount = it.players.size,
                            botCount = it.players.count { player -> player.isBot },
                            ginko = GameSettings.isEnabled(it.settings, GameSettings.GinkoMode),
                            offline = gameCode == "LOCAL",
                        )
                    }
                }
            },
            updatePlayerOrder = {
                launchGameAction("Couldn't update player order.") {
                    gameFlow.value?.orderedPlayerNames?.reversed()?.let { reversed ->
                        gameRepository.updatePlayerOrder(gameCode, reversed)
                    }
                }
            },
            onDiceRolled = { manuallyEntered ->
                launchGameAction("Couldn't roll. Check your connection and try again.") {
                    performRoll(manuallyEntered)
                }
            },
            onBank = { currentPlayer, bankingPlayerNames ->
                launchGameAction("Couldn't bank points. Check your connection and try again.") {
                    bankPlayers(gameFlow.value, currentPlayer, bankingPlayerNames)
                }
            },
            onRematch = {
                launchGameAction("Couldn't start rematch.") {
                    // Perks are re-decided for the rematch, exactly like game creation: owned
                    // Plus keeps them, otherwise a pending Plus Pass is spent to keep them, and
                    // with neither the rematch runs without perks. The pass that funded the
                    // original game never rides along for free.
                    val ownsHostPerks =
                        hostPerksActiveForCreatedGame(RemoveAdsPurchases.uiState.value)
                    val usePlusPass = !ownsHostPerks && PlusPass.pending.value
                    gameRepository.rematch(
                        gameCode,
                        hostPerksActive = ownsHostPerks || usePlusPass,
                    )
                    // A rematch is a new match on the same table. Reset per-match analytics guards
                    // so first actions and completion are attributed to the new game as well.
                    gameCompletionAnalyticsSent = false
                    firstRollLogged = false
                    firstBankLogged = false
                    if (usePlusPass && PlusPass.consume()) {
                        Analytics.plusPassUsed()
                    }
                    gameFlow.value?.let { game ->
                        Analytics.rematchStarted(
                            humanCount = game.players.count { !it.isBot },
                            botCount = game.players.count { it.isBot },
                        )
                    }
                }
            },
            onLeaveGame = {
                isLeavingGame = true
                val gameBeforeLeave = gameFlow.value
                val didLeave = runGameAction("Couldn't leave game.") {
                    val game = gameFlow.value ?: return@runGameAction
                    // Only a player on their own device can take over hosting: hostCreated
                    // players and bots are driven by whichever device is host.
                    val successor = game.orderedPlayerNames
                        .filter { it != playerName }
                        .mapNotNull { name -> game.players.firstOrNull { it.name == name } }
                        .firstOrNull { !it.hostCreated && !it.isBot }
                    when {
                        gameCode == "LOCAL" -> gameRepository.deleteGame(gameCode)
                        game.host != playerName -> gameRepository.removePlayer(gameCode, playerName)
                        successor != null -> gameRepository.removePlayer(gameCode, playerName)
                        else -> gameRepository.deleteGame(gameCode)
                    }
                }
                if (!didLeave) {
                    isLeavingGame = false
                } else if (
                    gameBeforeLeave != null &&
                    gameBeforeLeave.round == null &&
                    gameBeforeLeave.host == playerName
                ) {
                    Analytics.lobbyAbandoned(
                        humanCount = gameBeforeLeave.players.count { !it.isBot },
                        botCount = gameBeforeLeave.players.count { it.isBot },
                        offline = gameCode == GameRepository.LOCAL_GAME_CODE,
                    )
                }
                didLeave
            },
            onGameFinished = {
                launchGameAction("Couldn't finish game.") {
                    gameRepository.finishGame(gameCode)
                    AppBackgroundScope.launch {
                        delay(2000)
                        try {
                            gameRepository.deleteGame(gameCode)
                        } catch (_: Throwable) {
                            // Best effort: Firestore TTL still reaps abandoned or finished docs.
                        }
                    }
                }
            },
            onSettingChanged = { setting ->
                launchGameAction("Couldn't update setting.") {
                    gameFlow.value?.let { game ->
                        gameRepository.updateSetting(gameCode, game, setting)
                    }
                }
                // The UI passes the new value, so value == "true" here means Ginko Mode was just
                // turned on (not off).
                if (setting.name == GameSettings.GinkoMode.id &&
                    setting.value.toBooleanStrictOrNull() == true
                ) {
                    Analytics.ginkoEnabled()
                }
            },
            updatePlayerBankList = { playerName ->
                val currentList = bankListFlow.value.toMutableList()
                if (currentList.contains(playerName)) {
                    currentList.remove(playerName)
                } else {
                    currentList.add(playerName)
                }
                bankListFlow.value = currentList
            },
            onGlobalBankClicked = {
                launchGameAction("Couldn't bank points. Check your connection and try again.") {
                    gameFlow.value?.currentPlayer?.let { currentPlayer ->
                        bankPlayers(gameFlow.value, currentPlayer, bankListFlow.value)
                    }
                    bankListFlow.value = emptyList()
                }
            },
            addLocalPlayer = {
                launchGameAction("Couldn't add player.") {
                    val newPlayerNameInput = newPlayerNameInputFlow.value
                    newPlayerNameInputFlow.value = ""
                    val validation = PlayerNameValidator.validate(newPlayerNameInput)
                    if (validation is PlayerNameValidator.Result.Invalid) {
                        gameEventFlow.send(GameScreenEvent.ShowSnackbar(validation.reason))
                        return@launchGameAction
                    }
                    gameRepository.addPlayer(
                        gameCode = gameCode,
                        player = Player(
                            name = (validation as PlayerNameValidator.Result.Valid).name,
                            points = 0,
                            hostCreated = true
                        )
                    )
                }
            },
            addBot = {
                launchGameAction("Couldn't add bot.") {
                    gameRepository.addPlayer(
                        gameCode = gameCode,
                        player = Player(
                            name = generateBotName(gameFlow.value),
                            points = 0,
                            hostCreated = true,
                            isBot = true,
                        )
                    )
                }
            },
            makeHost = { playerName ->
                launchGameAction("Couldn't change host.") {
                    gameRepository.makeHost(gameCode, playerName)
                }
            },
            removePlayer = { playerName ->
                launchGameAction("Couldn't remove player.") {
                    // The removal (and whether it ends the game) is decided in one place: the
                    // data source, on authoritative state. A finishing removal drops endRoundNum
                    // so the remaining player sees the game-over screen, just like a normal end;
                    // the host then tears the game down via the finish button (onGameFinished).
                    gameRepository.removePlayer(gameCode, playerName)
                }
            },
            updateNewPlayerNameInput = { newPlayerName ->
                newPlayerNameInputFlow.value = newPlayerName
            },
            onScreenClosed = { LobbyAdState.release(this@BankoGameScreenUseCase) },
            onRoundOverlayShown = {
                roundOverlayStateFlow.value = roundOverlayStateFlow.value.copy(
                    showOverlay = false,
                    isAnimating = false
                )
            },
            onEndRoundNumChanged = { rounds ->
                launchGameAction("Couldn't update round count.") {
                    gameRepository.updateEndRoundNum(gameCode, rounds)
                }
            },
            onPublicVisibilityChanged = { isPublic ->
                launchGameAction("Couldn't update public visibility.") {
                    gameRepository.setGamePublic(gameCode, isPublic)
                }
            },
        )
    }

    /**
     * Buzzes this device when a fresh nudge is aimed at [playerName] (and nudging is enabled), and
     * starts a grace cooldown whenever the turn moves to a new player or the current player rolls,
     * so nobody can nudge the instant a turn begins or while the player is actively rolling.
     */
    private fun handleNudgeState(game: DomainGame?) {
        game ?: return

        val nudge = game.nudge
        if (!nudgeBaselineEstablished) {
            // First snapshot since opening the screen — adopt the current nonce (or null when no
            // nudge exists yet) silently so a stale nudge already in the document doesn't fire on
            // load. Must run even when there's no nudge, or the first real nudge looks like a
            // stale-on-load one and gets swallowed.
            lastSeenNudgeNonce = nudge?.nonce
            nudgeBaselineEstablished = true
        } else if (nudge != null && nudge.nonce > (lastSeenNudgeNonce ?: -1)) {
            lastSeenNudgeNonce = nudge.nonce
            if (nudge.targetPlayer == playerName &&
                GameSettings.isEnabled(game.settings, GameSettings.Nudging)
            ) {
                triggerNudgeHaptic()
                // Visible cue too, so the nudge lands on every platform (web has no vibration).
                nudgeReceivedFlow.value += 1
            }
        }

        // Restart the grace window when the turn changes hands OR when the current player rolls
        // (roundNum/currentRoll advance). Without the roll part, a player rolling back-to-back —
        // e.g. the last one left in the round, where the turn never changes — becomes nudgeable
        // mid-activity once the initial grace expires.
        val activityKey =
            "${game.currentPlayer}:${game.round?.roundNum}:${game.round?.currentRoll}"
        if (activityKey != previousNudgeActivityKey) {
            previousNudgeActivityKey = activityKey
            startNudgeCooldown(NUDGE_TURN_GRACE_MS)
        }
    }

    /**
     * Folds a finished game into this device's lifetime stats, from [playerName]'s seat. Practice
     * rounds are a tutorial against bots, so they never count.
     */
    private fun maybeRecordCareerGame(game: DomainGame) {
        if (game.round == null) {
            careerRecordedForThisGame = false
            return
        }
        if (careerRecordedForThisGame || isPracticeGame) return
        val completed = CompletedGame.from(
            game = game,
            selfName = playerName,
            online = gameCode != GameRepository.LOCAL_GAME_CODE,
            playedAtEpochSeconds = Timestamp.now().seconds,
        ) ?: return
        careerRecordedForThisGame = true
        val career = CareerStatsStore.record(completed)
        Analytics.careerGameRecorded(
            won = career.recentGames.firstOrNull()?.won == true,
            gamesPlayed = career.gamesPlayed,
            winStreak = career.currentWinStreak,
        )
    }

    /** Disables nudging for [durationMs], cancelling any prior cooldown. */
    private fun startNudgeCooldown(durationMs: Long) {
        nudgeCooldownJob?.cancel()
        canNudgeFlow.value = false
        nudgeCooldownJob = scope.launch {
            delay(durationMs)
            canNudgeFlow.value = true
        }
    }

    /** Adopts the initial event documents silently, then surfaces only replacements with fresh ids. */
    private fun handleTableReactionState(reactions: List<TableReaction>) {
        if (!reactionBaselineEstablished) {
            reactions.forEach { reaction ->
                lastSeenReactionEventBySender[reaction.senderKey()] = reaction.eventId
            }
            reactionBaselineEstablished = true
            return
        }

        reactions.forEach { reaction ->
            val senderKey = reaction.senderKey()
            if (lastSeenReactionEventBySender[senderKey] == reaction.eventId) return@forEach
            lastSeenReactionEventBySender[senderKey] = reaction.eventId

            val game = gameFlow.value
            if (tableReactionsMutedFlow.value || !isReactionForCurrentTurn(reaction, game)) {
                return@forEach
            }

            reactionAnimationSequence += 1
            val updated = mergeTableReactionBurst(
                current = visibleTableReactionsFlow.value,
                reaction = reaction,
                animationSequence = reactionAnimationSequence,
            )
            val retainedKeys = updated.mapTo(mutableSetOf()) { it.key }
            reactionExpiryJobs.keys.filterNot { it in retainedKeys }.forEach { droppedKey ->
                reactionExpiryJobs.remove(droppedKey)?.cancel()
            }
            visibleTableReactionsFlow.value = updated

            val burstKey = reaction.burstKey()
            reactionExpiryJobs.remove(burstKey)?.cancel()
            reactionExpiryJobs[burstKey] = scope.launch {
                delay(TABLE_REACTION_VISIBLE_MS)
                visibleTableReactionsFlow.value = visibleTableReactionsFlow.value.filterNot {
                    it.key == burstKey
                }
                reactionExpiryJobs.remove(burstKey)
            }
        }
    }

    private fun handleReactionTurnChange(game: DomainGame) {
        val round = game.round
        val turnKey = if (round == null) null else
            "${game.currentPlayer}:${round.roundNum}:${round.currentRoll}"
        if (previousReactionTurnKey != null && turnKey != previousReactionTurnKey) {
            clearVisibleTableReactions()
        }
        previousReactionTurnKey = turnKey
    }

    private fun clearVisibleTableReactions() {
        reactionExpiryJobs.values.forEach { it.cancel() }
        reactionExpiryJobs.clear()
        visibleTableReactionsFlow.value = emptyList()
    }

    private fun startTableReactionCooldown() {
        reactionCooldownJob?.cancel()
        canSendTableReactionFlow.value = false
        reactionCooldownJob = scope.launch {
            delay(TABLE_REACTION_COOLDOWN_MS)
            canSendTableReactionFlow.value = true
        }
    }

    private suspend fun performRoll(manuallyEntered: Int?) {
        val currentGame = gameFlow.value ?: return
        // Drop the roll if the snapshot hasn't advanced since our last dispatch — a previous roll is
        // still in flight, and computing this one off the same stale state would clobber it. Any
        // state change (our roll landing, or another player's turn) clears the block, so a stuck
        // pending round can never permanently lock out rolling.
        if (currentGame.round != null && currentGame.round == lastRolledFromRound) return

        val diceRoll = rollDice()
        val rolledValue = manuallyEntered ?: (diceRoll.dieOne + diceRoll.dieTwo)
        val rollDecision = BankoGameEngine.applyRoll(
            game = currentGame,
            diceRoll = diceRoll,
            manuallyEnteredValue = manuallyEntered,
            nextRoundModifier = nextRoundModifier(),
            nextGinkoEvent = nextGinkoEvent(currentGame),
        ) ?: return

        // "Lucky" achievement: this device's player rolling a 7 three times in a row. Tracked locally
        // so it works on every platform; Achievements mirrors it to Play Games on Android.
        if (currentGame.currentPlayer == playerName) {
            if (rolledValue == LUCKY_NUMBER) {
                consecutiveSevens++
                if (consecutiveSevens >= LUCKY_STREAK_NEEDED && Achievements.unlockLuckyDice()) {
                    gameEventFlow.send(GameScreenEvent.ShowSnackbar(LUCKY_DICE_UNLOCKED_MESSAGE))
                }
            } else {
                consecutiveSevens = 0
            }
        }

        lastRolledFromRound = currentGame.round
        try {
            gameRepository.rollDice(
                gameCode,
                currentGame.round,
                rollDecision.round,
                rollDecision.nextPlayerName,
                rolledValue,
            )
            val currentPlayerIsBot = currentGame.players
                .firstOrNull { it.name == currentGame.currentPlayer }
                ?.isBot == true
            if (!firstRollLogged && !currentPlayerIsBot) {
                firstRollLogged = true
                Analytics.firstRoll(
                    role = analyticsRole(currentGame),
                    offline = gameCode == GameRepository.LOCAL_GAME_CODE,
                    manual = manuallyEntered != null,
                )
            }
        } catch (e: Throwable) {
            // The write never landed, so the snapshot won't change — let the player retry.
            lastRolledFromRound = null
            throw e
        }
    }

    /**
     * The modifier the next round should open under. Limited-time modes take precedence because they
     * define the active event rules; Ginko otherwise keeps normal rounds in the mix so the twists
     * have contrast, with a guaranteed round-level curveball in the final round.
     */
    private fun nextRoundModifier(): RoundModifier {
        val game = gameFlow.value
        LimitedTimeGameModes.roundModifierFor(game?.settings ?: emptyList())?.let {
            return it
        }

        val ginkoEnabled = game?.settings?.let {
            GameSettings.isEnabled(it, GameSettings.GinkoMode)
        } ?: false
        if (!ginkoEnabled) return RoundModifier.NONE

        val upcomingRound = (game.round?.roundNum ?: 0) + 1
        return if (upcomingRound >= game.endRoundNum) {
            RoundModifier.pickChaos()
        } else {
            RoundModifier.pick(enabled = true, chancePercent = ROUND_MODIFIER_CHANCE_PERCENT)
        }
    }

    /**
     * A possible mid-round interruption after the roll being dispatched survives. These only appear
     * once the safe opening has passed, and the chance ramps up as the pot and roll count climb.
     */
    private fun nextGinkoEvent(game: DomainGame): GinkoEvent {
        val round = game.round ?: return GinkoEvent.NONE
        val ginkoEnabled = GameSettings.isEnabled(game.settings, GameSettings.GinkoMode)
        if (!ginkoEnabled) return GinkoEvent.NONE
        if (round.ginkoEvent != GinkoEvent.NONE) return GinkoEvent.NONE
        if (round.currentRoll < FIRST_GINKO_EVENT_ROLL) return GinkoEvent.NONE

        val maxEvents = if (round.roundNum >= game.endRoundNum - 1) 2 else 1
        if (round.ginkoEventsTriggered >= maxEvents) return GinkoEvent.NONE

        val chance = ginkoEventChancePercent(game, round)
        return if (Random.nextInt(100) < chance) GinkoEvent.pick() else GinkoEvent.NONE
    }

    private fun ginkoEventChancePercent(game: DomainGame, round: Round): Int {
        val rollPressure = ((round.currentRoll - FIRST_GINKO_EVENT_ROLL) * 6).coerceIn(0, 24)
        val potPressure = when {
            round.currentPoints >= 300 -> 28
            round.currentPoints >= 200 -> 22
            round.currentPoints >= 100 -> 14
            round.currentPoints >= 60 -> 8
            else -> 0
        }
        val endgamePressure = if (round.roundNum >= game.endRoundNum - 1) 12 else 0
        return (BASE_GINKO_EVENT_CHANCE_PERCENT + rollPressure + potPressure + endgamePressure)
            .coerceAtMost(MAX_GINKO_EVENT_CHANCE_PERCENT)
    }

    /**
     * If a bot is the current player in a (local) started game, take its turn after a short,
     * randomized pause so it doesn't act instantly. Re-entrancy is guarded by a per-turn signature,
     * and the pending job is cancelled whenever the turn moves on.
     */
    private fun maybeScheduleBotTurn(game: DomainGame?) {
        // Bots are driven by a single authoritative device so they act exactly once: the only device
        // in a local game, or the host in an online game (the host already acts for device-less
        // players). Non-host devices just observe the bot's moves syncing through Firestore.
        val isBotDriver = gameCode == "LOCAL" || (game != null && game.host == playerName)
        if (!isBotDriver) {
            botJob?.cancel()
            botJob = null
            scheduledBotTurn = null
            return
        }

        val round = game?.round
        val currentPlayer = game?.players?.firstOrNull { it.name == game.currentPlayer }
        val isBotTurn = round != null &&
                !game.finished &&
                round.roundNum <= game.endRoundNum &&
                currentPlayer?.isBot == true

        if (!isBotTurn) {
            botJob?.cancel()
            botJob = null
            scheduledBotTurn = null
            return
        }

        val activeRound = round
        val activePlayer = currentPlayer
        val turnSignature = "${activeRound.roundNum}:${activeRound.currentRoll}:${activePlayer.name}"
        if (turnSignature == scheduledBotTurn) return
        scheduledBotTurn = turnSignature

        botJob?.cancel()
        botJob = scope.launch {
            // Wait for the previous roll's animation to finish (it starts the moment the turn passes
            // to the bot), then add a short randomized "thinking" pause so the bot doesn't roll over
            // the animation that's still playing.
            delay(ROLL_ANIMATION_MS + Random.nextLong(BOT_THINK_MIN_MS, BOT_THINK_MAX_MS))

            // If this turn opened a new round, the round overlay is up (a longer, variable
            // bust-delay + hold + fade animation — extra-long for Ginko modifier rounds). Wait for
            // it to fully clear so the bot doesn't roll over it. Returns immediately for ordinary
            // within-round turn passes, where no overlay is showing.
            roundOverlayStateFlow.first { !it.showOverlay }

            // The board may have moved on during the pause — only act if this is still the same turn.
            val latest = gameFlow.value ?: return@launch
            val latestRound = latest.round ?: return@launch
            if ("${latestRound.roundNum}:${latestRound.currentRoll}:${latest.currentPlayer}" != turnSignature) {
                return@launch
            }

            when (BankoBot.decide(latest)) {
                BotAction.Roll -> runGameAction("Couldn't roll for bot.") {
                    performRoll(manuallyEntered = null)
                }
                BotAction.Bank -> runGameAction("Couldn't bank for bot.") {
                    bankPlayers(
                        currentGame = latest,
                        currentPlayer = activePlayer.name,
                        bankingPlayerNames = listOf(activePlayer.name),
                        isBotAction = true,
                    )
                }
            }
        }
    }

    private fun generateBotName(game: DomainGame?): String {
        val takenNames = game?.players?.map { it.name }?.toSet() ?: emptySet()
        var index = 1
        while ("CPU $index" in takenNames) index++
        return "CPU $index"
    }

    private suspend fun bankPlayers(
        currentGame: DomainGame?,
        currentPlayer: String,
        bankingPlayerNames: List<String>,
        isBotAction: Boolean = false,
    ) {
        currentGame ?: return
        currentGame.round ?: return
        // The host may bank for other active players. A guest may only bank themselves on their own
        // turn, matching the Firestore turn/score authorization and avoiding a guaranteed retry error.
        if (
            currentGame.host != playerName &&
            (!canPlayerBankForSelf(currentGame, playerName) || bankingPlayerNames.any { it != playerName })
        ) return
        val bankDecision = BankoGameEngine.bank(currentGame, currentPlayer, bankingPlayerNames) ?: return

        gameRepository.bankPoints(
            gameCode = gameCode,
            bankingPlayers = bankDecision.bankingPlayers,
            nextRoundModifier = nextRoundModifier(),
        )
        if (!firstBankLogged && !isBotAction) {
            firstBankLogged = true
            Analytics.firstBank(
                role = analyticsRole(currentGame),
                offline = gameCode == GameRepository.LOCAL_GAME_CODE,
                points = currentGame.round.currentPoints,
            )
        }
    }

    private fun analyticsRole(game: DomainGame): String =
        if (game.host == playerName) "host" else "guest"

    private fun launchGameAction(message: String, block: suspend () -> Unit) {
        scope.launch {
            runGameAction(message, block)
        }
    }

    private suspend fun runGameAction(message: String, block: suspend () -> Unit): Boolean {
        try {
            block()
            return true
        } catch (e: CancellationException) {
            throw e
        } catch (e: Throwable) {
            gameEventFlow.send(GameScreenEvent.ShowSnackbar(e.userFacingGameActionMessage(message)))
            return false
        }
    }

    private fun rollDice(): DiceRoll {
        return DiceRoll(
            dieOne = Random.nextInt(1, 7),
            dieTwo = Random.nextInt(1, 7),
        )
    }

    private companion object {
        // Covers the 500ms reset + 900ms 3D tumble, with a little settling margin.
        // Bots wait that out before their randomized "thinking" pause so they never roll over it.
        const val ROLL_ANIMATION_MS = 1500L
        const val BOT_THINK_MIN_MS = 500L
        const val BOT_THINK_MAX_MS = 1200L
        const val ROUND_MODIFIER_CHANCE_PERCENT = 35
        const val FIRST_GINKO_EVENT_ROLL = 4
        const val BASE_GINKO_EVENT_CHANCE_PERCENT = 18
        const val MAX_GINKO_EVENT_CHANCE_PERCENT = 70

        // Nudge pacing: a grace period at the start of each turn, then a cooldown between nudges.
        const val NUDGE_TURN_GRACE_MS = 8_000L
        const val NUDGE_COOLDOWN_MS = 15_000L
        const val TABLE_REACTION_COOLDOWN_MS = 4_000L
        const val TABLE_REACTION_VISIBLE_MS = 1_800L

        // "Lucky" achievement: roll a 7 this many times in a row.
        const val LUCKY_NUMBER = 7
        const val LUCKY_STREAK_NEEDED = 3
        const val LUCKY_DICE_UNLOCKED_MESSAGE = "Lucky dice unlocked!"
        const val CREATOR_DICE_UNLOCKED_MESSAGE = "Creator dice unlocked!"
    }
}

// Shown by the screen (not the use case) when practice completes, so it rides the global snackbar
// host and stays visible across the navigation back to the start screen.
internal const val PRACTICE_DICE_UNLOCKED_MESSAGE = "Training Wheels dice unlocked!"

// One-time first-game teaching lines, delivered through the commentary ticker at the moment the
// banking decision first goes live (and, if ignored, right after the first bust it caused).
internal const val BANKING_HINT_MESSAGE =
    "The pot's live — a 7 wipes it now! Tap ¡Banko! to keep your share."
internal const val BANKING_BUST_MESSAGE =
    "Busted! That's what ¡Banko! is for — bank before the 7 lands."

data class BankoGameScreenUiState(
    val gameCode: String,
    val playerName: String,
    // True when this game is the practice round: the screen suppresses the end-game interstitial
    // and review prompt, and completion returns to the start screen instead of game-over.
    val isPracticeGame: Boolean,
    val gameFlow: StateFlow<DomainGame?>,
    val bankListFlow: StateFlow<List<String>>,
    val gameEventFlow: EventStateFlow<GameScreenEvent>,
    val newPlayerNameInputFlow: StateFlow<String>,
    val roundOverlayStateFlow: StateFlow<RoundOverlayState>,
    val commentaryFlow: StateFlow<String?>,
    val canNudgeFlow: StateFlow<Boolean>,
    val nudgeReceivedFlow: StateFlow<Int>,
    val canSendTableReactionFlow: StateFlow<Boolean>,
    val tableReactionsMutedFlow: StateFlow<Boolean>,
    val visibleTableReactionsFlow: StateFlow<List<TableReactionBurst>>,
    val onCommentaryLineFinished: (String) -> Unit,
    val onNudge: () -> Unit,
    val onTableReaction: (TableReactionKind) -> Unit,
    val onToggleTableReactionsMuted: () -> Unit,
    val onStartGame: () -> Unit,
    val updatePlayerOrder: () -> Unit,
    val onDiceRolled: (Int?) -> Unit,
    val onBank: (String, List<String>) -> Unit,
    val onRematch: () -> Unit,
    val onLeaveGame: suspend () -> Boolean,
    val onGameFinished: () -> Unit,
    val onSettingChanged: (Setting) -> Unit,
    val updatePlayerBankList: (String) -> Unit,
    val onGlobalBankClicked: () -> Unit,
    val addLocalPlayer: () -> Unit,
    val addBot: () -> Unit,
    val makeHost: (String) -> Unit,
    val removePlayer: (String) -> Unit,
    val updateNewPlayerNameInput: (String) -> Unit,
    // The screen is going away for good: turns ads back on unless a newer game screen has opened.
    val onScreenClosed: () -> Unit,
    val onRoundOverlayShown: () -> Unit,
    val onEndRoundNumChanged: (Int) -> Unit,
    val onPublicVisibilityChanged: (Boolean) -> Unit,
)

/** A presentation burst; equal kinds aimed at the same player collapse into a visible count. */
data class TableReactionBurst(
    val key: String,
    val targetPlayer: String,
    val kind: TableReactionKind,
    val count: Int,
    val animationSequence: Int,
)

internal fun mergeTableReactionBurst(
    current: List<TableReactionBurst>,
    reaction: TableReaction,
    animationSequence: Int,
    maxVisible: Int = 3,
): List<TableReactionBurst> {
    val key = reaction.burstKey()
    val existingIndex = current.indexOfFirst { it.key == key }
    if (existingIndex >= 0) {
        return current.toMutableList().apply {
            this[existingIndex] = this[existingIndex].copy(
                count = this[existingIndex].count + 1,
                animationSequence = animationSequence,
            )
        }
    }
    return (current + TableReactionBurst(
        key = key,
        targetPlayer = reaction.targetPlayer,
        kind = reaction.kind,
        count = 1,
        animationSequence = animationSequence,
    )).takeLast(maxVisible)
}

internal fun isReactionForCurrentTurn(reaction: TableReaction, game: DomainGame?): Boolean {
    val round = game?.round ?: return false
    return !game.finished &&
        round.roundNum <= game.endRoundNum &&
        reaction.targetPlayer == game.currentPlayer &&
        reaction.roundNum == round.roundNum &&
        reaction.currentRoll == round.currentRoll
}

private fun TableReaction.senderKey(): String = ownerUid.ifBlank { senderPlayer }
private fun TableReaction.burstKey(): String = "$targetPlayer:${kind.name}"

data class RoundOverlayState(
    val showOverlay: Boolean = false,
    val roundNumber: Int = 1,
    val isAnimating: Boolean = false,
    val modifier: RoundModifier = RoundModifier.NONE,
)

private fun Throwable.userFacingGameActionMessage(fallback: String): String {
    val raw = message.orEmpty()
    return when {
        raw.contains("permission", ignoreCase = true) ->
            "That action isn't allowed in this game anymore."
        raw.contains("not_found", ignoreCase = true) ||
            raw.contains("not found", ignoreCase = true) ->
            "That game is no longer available."
        raw.contains("network", ignoreCase = true) ||
            raw.contains("unavailable", ignoreCase = true) ->
            "$fallback Check your connection and try again."
        else -> fallback
    }
}
